import { useRestApiProvider } from "@jugl-web/rest-api";
import { Alert, PageLoaderFull } from "@jugl-web/ui-kit";
import { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { Stripe, StripeElements, loadStripe } from "@stripe/stripe-js";
import { UpdateCardAlertResult } from "@web-src/modules/entities/providers/EntitySubscriptionProvider/components/UpdateCardAlert/types";

const UPDATE_CARD_V2_FORM_ID = "update-card-v2-form";

export type UpdateCardV2AlertProps = { title?: string; entityId: string };

export type UpdateCardV2AlertHandle = {
  start: () => Promise<UpdateCardAlertResult>;
};

export const UpdateCardV2Alert = forwardRef<
  UpdateCardV2AlertHandle,
  UpdateCardV2AlertProps
>(({ title, entityId }, ref) => {
  const { subscriptionV2Api } = useRestApiProvider();
  const [setupIntent] = subscriptionV2Api.useLazySetupIntentQuery();
  const [setupIntentCallback] =
    subscriptionV2Api.useSetupIntentCallbackMutation();

  const startResolveFunc = useRef<
    ((value: UpdateCardAlertResult) => void) | null
  >(null);
  const stripeObjects = useRef<{
    stripe: Stripe;
    elements: StripeElements;
    clientSecret: string;
  } | null>(null);

  const [isInProgress, setIsInProgress] = useState(false);
  const [isUpdateAlertOpen, setIsUpdateAlertOpen] = useState(false);
  const [isStripeLoading, setIsStripeLoading] = useState(false);
  const [stripeErrorMessage, setStripeErrorMessage] = useState<string>();

  const finish = (result: UpdateCardAlertResult) => {
    startResolveFunc.current?.(result);
    if (isStripeLoading) {
      setIsStripeLoading(false);
    }
    if (isUpdateAlertOpen) {
      setIsUpdateAlertOpen(false);
    }
    if (stripeErrorMessage) {
      setStripeErrorMessage(undefined);
    }
    startResolveFunc.current = null;
    setIsInProgress(false);
  };

  const proceedCardUpdate = async () => {
    try {
      const response = await setupIntent({ entityId });
      if ("error" in response && response.error) {
        finish({ result: "apiError", error: response.error });
        return;
      }
      if (!("data" in response) || !response.data) {
        finish({ result: "apiError" });
        return;
      }
      setIsStripeLoading(true);
      const stripe = await loadStripe(response.data.pub_key);
      if (!stripe) {
        finish({ result: "stripeError" });
        return;
      }
      const elements = stripe.elements({
        clientSecret: response.data.client_secret,
        loader: "auto",
      });
      const paymentElement = elements.create("payment");
      setIsUpdateAlertOpen(true);
      await new Promise((r) => {
        setTimeout(r, 200);
      });
      stripeObjects.current = {
        stripe,
        elements,
        clientSecret: response.data.client_secret,
      };
      paymentElement.mount(`#${UPDATE_CARD_V2_FORM_ID}`);
      setIsStripeLoading(false);
    } catch (error) {
      finish({ result: "unknownError", error });
    }
  };

  const handleConfirmCardClick = async () => {
    if (stripeObjects.current === null) {
      return;
    }
    if (stripeErrorMessage) {
      setStripeErrorMessage(undefined);
    }
    try {
      setIsStripeLoading(true);
      const { stripe, elements, clientSecret } = stripeObjects.current;
      await elements.submit();
      const { error, setupIntent: confirmedSetup } = await stripe.confirmSetup({
        elements,
        clientSecret,
        confirmParams: {
          return_url: "http://api-dev.jugl.com/stripe/callback/setup_intent",
        },
        redirect: "if_required",
      });
      if (error) {
        setStripeErrorMessage(
          error.message || "Something went wrong, please try again later"
        );
        return;
      }
      if (confirmedSetup?.id) {
        await setupIntentCallback({ setupIntentId: confirmedSetup.id });
      }
      finish({ result: "success" });
    } catch (error) {
      finish({ result: "unknownError", error });
    } finally {
      setIsStripeLoading(false);
    }
  };

  useImperativeHandle(ref, () => ({
    start: () =>
      new Promise<UpdateCardAlertResult>((resolve) => {
        setIsInProgress(true);
        startResolveFunc.current = resolve;
        proceedCardUpdate();
      }),
  }));

  if (!isInProgress) {
    return null;
  }
  return (
    <>
      <PageLoaderFull
        isActive={isStripeLoading && !isUpdateAlertOpen}
        isTransparent
      />
      <Alert
        isOpen={isUpdateAlertOpen}
        title={title || "Provide your card details"}
        content={
          <>
            <form className="py-5" id={UPDATE_CARD_V2_FORM_ID} />
            {!!stripeErrorMessage && (
              <div className="mt-2 text-left text-sm text-red-500">
                {stripeErrorMessage}
              </div>
            )}
          </>
        }
        onRequestClose={() => {
          finish({ result: "cancelled" });
        }}
        buttons={[
          {
            text: "Cancel",
            role: "close",
          },
          {
            text: "Continue",
            isDisabled: isStripeLoading,
            onClick: handleConfirmCardClick,
          },
        ]}
      />
    </>
  );
});
